import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AREAS, areaBySlug } from '../data/areas';
import { areaJsonLd } from '../data/areaSchema';
import { AREA_STEPS } from '../data/howItWorks';
import { guideLinks } from '../data/guides';
import { jobLinks } from '../data/jobs';
import Seo from '../components/Seo';
import BrandName from '../components/BrandName';
import { RichText } from '../components/RichText';
import NotFoundPage from './NotFoundPage';
import './AreaPage.css';

const STEPS = AREA_STEPS;

export { RichText };

function Paragraphs({ blocks }) {
  return blocks.map((block, i) => (
    <p key={i}><RichText value={block} /></p>
  ));
}

const postJobPath = (user) => {
  if (!user) return '/signup';
  if (user.type === 'provider') return '/provider-dashboard';
  return '/customer-dashboard';
};

const AreaPage = () => {
  const { slug } = useParams();
  const area = areaBySlug(slug);
  const { user } = useAuth();

  if (!area) return <NotFoundPage />;

  const nearby = area.nearby
    .map((id) => AREAS.find((item) => item.slug === id))
    .filter(Boolean);
  const h1Lead = area.h1.replace(area.name, '').trim();

  return (
    <div className="area-page">
      <Seo
        title={area.title}
        description={area.description}
        path={area.path}
        jsonLd={areaJsonLd(area)}
      />

      <header className="area-hero">
        <div className="hero-bg-lines" aria-hidden="true" />
        <div className="area-hero-content">
          <div className="hero-eyebrow">{area.eyebrow}</div>
          <h1>{h1Lead} <span>{area.name}</span></h1>
          <Paragraphs blocks={area.intro} />
        </div>
      </header>

      <div className="page-wrapper">
        <section className="area-block" aria-labelledby="how-heading">
          <h2 id="how-heading">How <BrandName inline /> <span>works</span></h2>
          <div className="area-card">
            <p>{area.howLead}</p>
            <p className="area-note">
              The WasteGame is new. A posted job is a request for prices. It is not a booking, and it is not a promise that a carrier will take the job.
            </p>
          </div>
          <div className="area-steps area-follow">
            {STEPS.map((step) => (
              <article key={step.n} className="area-step">
                <div className="area-step-n">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="area-block" aria-labelledby="jobs-heading">
          <h2 id="jobs-heading">Jobs people post in <span>{area.name}</span></h2>
          <div className="area-jobs">
            {area.jobs.map((job) => (
              <article key={job.title} className="area-card area-job">
                <h3>{job.icon} {job.title}</h3>
                <p>{job.text}</p>
              </article>
            ))}
          </div>
          <p className="area-services">
            On the site these sit with{' '}
            <Link to="/services#waste-collection">waste collection</Link>,{' '}
            <Link to="/services#garden-clearance">garden clearance</Link> and{' '}
            <Link to="/services#house-clearance">house clearance</Link>.
          </p>
        </section>

        <section className="area-block" aria-labelledby="local-heading">
          <h2 id="local-heading">{area.localTitle}</h2>
          <div className="area-card area-prose">
            <Paragraphs blocks={area.local} />
          </div>
        </section>

        <section className="area-cta area-block" aria-labelledby="cta-heading">
          <h2 id="cta-heading">Post your job <span>free</span></h2>
          <p>
            Describe the waste in {area.name}. Registered waste carriers can send a price. You pay by card only if you accept one and the job is marked complete.
          </p>
          <div className="area-cta-actions">
            <Link to={postJobPath(user)} className="btn btn-primary">Post your job free</Link>
            <Link to="/areas" className="btn btn-secondary">All areas</Link>
          </div>
        </section>

        <section className="area-block" aria-labelledby="faq-heading">
          <h2 id="faq-heading">Questions about <span>{area.name}</span></h2>
          <div className="area-faq">
            {area.faqs.map((faq) => (
              <details key={faq.q} className="area-faq-item">
                <summary>{faq.q}</summary>
                <div className="area-answer">
                  <p><RichText value={faq.a} /></p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {nearby.length > 0 && (
          <section className="area-block" aria-labelledby="nearby-heading">
            <h2 id="nearby-heading">Nearby <span>areas</span></h2>
            <div className="area-nearby">
              {nearby.map((item) => (
                <Link key={item.slug} to={item.path} className="area-chip">{item.name}</Link>
              ))}
            </div>
          </section>
        )}

        <section className="area-block" aria-labelledby="related-heading">
          <h2 id="related-heading">Job pages and <span>guides</span></h2>
          <p className="area-services">
            The Swansea job pages are about one kind of collection. The same posting rules apply in {area.name}. Posting is free. It does not book a van.
          </p>
          <div className="area-nearby area-follow">
            {jobLinks().map((item) => (
              <Link key={item.href} to={item.href} className="area-chip">{item.label}</Link>
            ))}
            {guideLinks().map((item) => (
              <Link key={item.href} to={item.href} className="area-chip">{item.label}</Link>
            ))}
          </div>
        </section>

        <p className="area-services">
          Registered waste carrier? <Link to="/provider-signup">Create a carrier account</Link> if you want to send prices.
        </p>
      </div>
    </div>
  );
};

export default AreaPage;
